import { useEffect } from "react";

/**
 * Lightweight, dependency-free per-page SEO for this SPA.
 *
 * Updates the document title and the head meta/canonical tags on route change
 * so each page exposes a unique title, description, canonical URL and social
 * preview — without pulling in react-helmet.
 *
 * Usage:
 *   useSEO({
 *     title: "Become a Vendor",
 *     description: "List your event business on MandapAI...",
 *     path: "/vendor-registration",
 *   });
 */
const SITE_NAME = "MandapAI";
const BASE_URL = "https://mandapai.in";
const DEFAULT_IMAGE = `${BASE_URL}/og-image.jpg`;
const DEFAULT_TITLE = "MandapAI — India's AI-Powered Event & Wedding Vendor Marketplace";

const upsertMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

export default function useSEO({
  title,
  description,
  path = "",
  image = DEFAULT_IMAGE,
  noindex = false,
} = {}) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
    const url = `${BASE_URL}${path}`;

    document.title = fullTitle;

    upsertMeta("name", "description", description);
    upsertLink("canonical", url);
    upsertMeta(
      "name",
      "robots",
      noindex
        ? "noindex, nofollow"
        : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
    );

    // Open Graph
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", image);

    // Twitter
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);
  }, [title, description, path, image, noindex]);
}
